"use client";

import { Button, Field, Flex, Input, Stack, Text } from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";

import PhoneInput from "@components/common/PhoneInput";
import { isValidPhone } from "lib/phoneUtils";

interface EmployeeFormValues {
  name: string;
  phoneNumber: string;
  email: string;
}

interface EmployeeCreateFormProps {
  onSubmit: (values: EmployeeFormValues) => Promise<void>;
  onCancel: () => void;
}

const EmployeeCreateForm = ({
  onSubmit,
  onCancel,
}: EmployeeCreateFormProps) => {
  const [values, setValues] = useState<EmployeeFormValues>({
    name: "",
    phoneNumber: "",
    email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [phoneBlurred, setPhoneBlurred] = useState(false);

  const onPhoneBlur = useCallback(() => {
    setPhoneBlurred(true);
  }, []);

  const onChangeField = useMemo(() => {
    const handler =
      (field: keyof EmployeeFormValues) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setValues((prev) => ({ ...prev, [field]: e.target.value }));
      };
    return {
      name: handler("name"),
      email: handler("email"),
    };
  }, []);

  const onPhoneChange = useCallback((val: string) => {
    setValues((prev) => ({ ...prev, phoneNumber: val }));
  }, []);

  const phoneValid = isValidPhone(values.phoneNumber);
  const showPhoneError =
    phoneBlurred && values.phoneNumber !== "" && !phoneValid;

  const canSubmit =
    values.name.trim().length > 0 && phoneValid;

  const onClickSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, values]);

  return (
    <Stack gap={3} p={4} bg="bg.muted" borderRadius="lg">
      <Text fontSize="sm" fontWeight="semibold">
        New Employee
      </Text>
      <Field.Root required>
        <Field.Label>
          Name <Field.RequiredIndicator />
        </Field.Label>
        <Input
          size="sm"
          placeholder="Employee name"
          value={values.name}
          onChange={onChangeField.name}
          borderColor="border.light"
        />
      </Field.Root>
      <Field.Root required invalid={showPhoneError}>
        <Field.Label>
          Phone number <Field.RequiredIndicator />
        </Field.Label>
        <PhoneInput
          size="sm"
          value={values.phoneNumber}
          onChange={onPhoneChange}
          onBlur={onPhoneBlur}
          borderColor="border.light"
        />
        {showPhoneError && (
          <Field.ErrorText>Enter a valid phone number</Field.ErrorText>
        )}
      </Field.Root>
      <Field.Root>
        <Field.Label>Email</Field.Label>
        <Input
          size="sm"
          placeholder="Email"
          value={values.email}
          onChange={onChangeField.email}
          borderColor="border.light"
        />
      </Field.Root>
      <Flex gap={2} justify="flex-end">
        <Button size="2xs" variant="secondaryOutline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="2xs"
          onClick={onClickSubmit}
          disabled={!canSubmit || submitting}
          loading={submitting}
          loadingText="Creating"
        >
          Create
        </Button>
      </Flex>
    </Stack>
  );
};

export default EmployeeCreateForm;
